import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { AppConfig } from '../../../core-bundle/config/app-config';

import Analytics from '@module/analytics';
import I18n from '@module/i18n';
import Router from '@module/router';

import Actions from '@app/store/action';
import generatePseudoRandomNumber from '@app/helper/generate-pseudo-random-number';
import { isLoggedInFreeUser, userNeedsTips } from '@app/helper/user';

import PopTip from '@component/info/poptip';
import Icon from '@component/icon';
import Radio from '@component/form/element/radio';

import { SUN, WOODSMOKE } from '@constant/colors';
import {
  TAB,
  ESCAPE,
  ESC,
  ARROWUP,
  UP,
  ARROWDOWN,
  DOWN,
  ENTER,
  SPACE,
  SPACEBAR,
  EMPTYSPACE,
} from '@constant/keyboard-keys';

export class Options extends React.Component {
  transferTypeNodes = {
    email: React.createRef(),
    link: React.createRef(),
  };
  transferExpiryOptionsRef = React.createRef();
  expiryOptionsMenuRef = React.createRef();
  passwordRef = React.createRef();

  state = {
    focusedOptionIndex: 0,
    isPoptipVisible: false,
  };

  // The expiry options come in a random order from the backend.
  // We want to sort them from short to long duration, as this is
  // how we use them in the dropdown options.
  expiryOptions = AppConfig.transferExpiryOptions
    .sort((optionA, optionB) => {
      if (optionA === optionB) {
        return 0;
      }
      const numberOptionA = parseInt(optionA);
      const numberOptionB = parseInt(optionB);
      if (isNaN(numberOptionA)) {
        return 1;
      }
      if (isNaN(numberOptionB)) {
        return -1;
      }
      return numberOptionA - numberOptionB;
    })
    .reduce(
      (optionsObject, option) => ({
        ...optionsObject,
        [option]: I18n.t(`transfer.options.expire_in.${option}`),
      }),
      {}
    );

  lastIndexOfExpiryOptions = Object.keys(this.expiryOptions).length - 1;

  componentDidUpdate(prevProps) {
    const { visible } = this.props.ui.uploader.options;
    if (
      prevProps.ui.uploader.options.visible !== visible &&
      visible === true &&
      this.transferTypeNodes.email.current
    ) {
      this.transferTypeNodes.email.current.focus();
    }
  }

  getSelectedExpiryIndex = () => {
    const expiryOptionKeys = Object.keys(this.expiryOptions);
    return expiryOptionKeys.findIndex((option) => this.props.expiry === option);
  };

  switchTransferType(newType) {
    const { changeType, expiry, onTransferType, password, type } = this.props;
    if (type === newType) {
      return;
    }

    Analytics.trackGoogleEvent({
      category: 'Transfer',
      action: 'Preferences - Switch Transfer Type',
      label: `Type-${newType} Password-${
        password ? 'yes' : 'no'
      } Expiry-${expiry}`,
    });

    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_more_button',
      label: `send_as_${newType}`,
    });

    changeType(newType);
    if (onTransferType) {
      onTransferType(newType);
    }
  }

  changeExpiry = (newExpiry) => {
    const { changeExpiry, password, type } = this.props;

    Analytics.trackGoogleEvent({
      category: 'Transfer',
      action: 'Preferences - Change Expiry',
      label: `Type-${type} Password-${
        password ? 'yes' : 'no'
      } Expiry-${newExpiry}`,
    });

    Analytics.trackSnowplowEvent({
      category: 'transfer_window',
      action: 'click_dotdotdot',
      label: `expire_after_${newExpiry}`,
      property: 'pro_account',
    });

    changeExpiry(newExpiry);
  };

  changePassword = (e) => {
    this.props.changePassword(e.target.value);
  };

  showPlusTooltip = () => {
    const { pushTransferNotification, user } = this.props;
    if (user.loggedIn && !isLoggedInFreeUser(user)) {
      return;
    }

    clearTimeout(this.timer);

    pushTransferNotification({ text: 'PlusUpsell' });
  };

  gotoPlus(e, user, type) {
    e.preventDefault();

    const isFreeAccountUser = isLoggedInFreeUser(user);
    const types = {
      expiry: isFreeAccountUser
        ? 'WT202005_settings_expiry_fa_to_pro'
        : 'WT201610_Settings_Expiry',
      password: isFreeAccountUser
        ? 'WT202005_settings_password_fa_to_pro'
        : 'WT201610_Settings_Password',
    };

    Router.navigateTo('/pro', false, { search: 'trk=' + types[type] });
  }

  showTransferTypeTip = () => {
    if (userNeedsTips(this.props.user)) {
      this.props.pushTransferNotification({ text: 'TipTransferType' });
    }
  };

  hideTransferTypeTip = () => {
    this.props.clearTransferNotification();
  };

  onOptionKeyDown = (event, expiryKey) => {
    const tooltipListItems = Array.from(
      this.expiryOptionsMenuRef.current.querySelectorAll('li')
    );

    event.preventDefault();

    switch (event.key) {
      case ESCAPE:
      case ESC:
        this.setState({ isPoptipVisible: false }, () =>
          this.transferExpiryOptionsRef.current.focus()
        );
        break;
      case ARROWUP:
      case UP:
        this.setState(
          (prevState) => ({
            focusedOptionIndex:
              prevState.focusedOptionIndex > 0
                ? prevState.focusedOptionIndex - 1
                : this.lastIndexOfExpiryOptions,
          }),
          () => tooltipListItems[this.state.focusedOptionIndex].focus()
        );
        break;
      case ARROWDOWN:
      case DOWN:
        this.setState(
          (prevState) => ({
            focusedOptionIndex:
              prevState.focusedOptionIndex < this.lastIndexOfExpiryOptions
                ? prevState.focusedOptionIndex + 1
                : 0,
          }),
          () => tooltipListItems[this.state.focusedOptionIndex].focus()
        );
        break;
      case ENTER:
        this.changeExpiry(expiryKey);
        this.setState({ isPoptipVisible: false }, () =>
          this.transferExpiryOptionsRef.current.focus()
        );
        break;
    }

    // Jump back into the transfer option flow when tabbing inside the expiry dropdown
    if (event.key === TAB && event.shiftKey) {
      this.setState({ isPoptipVisible: false }, () =>
        this.transferTypeNodes.link.current.focus()
      );
    } else if (event.key === TAB) {
      this.setState({ isPoptipVisible: false }, () =>
        this.passwordRef.current.focus()
      );
    }
  };

  onTransferExpiryKeyDown = (event) => {
    switch (event.key) {
      case SPACE:
      case SPACEBAR:
      case EMPTYSPACE:
      case ARROWDOWN:
      case DOWN:
      case ENTER:
        this.setState(
          (prevState) => ({
            isPoptipVisible: !prevState.isPoptipVisible,
            focusedOptionIndex: this.getSelectedExpiryIndex(),
          }),
          () => {
            const tooltipListItems = Array.from(
              this.expiryOptionsMenuRef.current.querySelectorAll('li')
            );
            tooltipListItems[this.state.focusedOptionIndex].focus();
          }
        );
    }
  };

  renderExpiry(user) {
    if (!user.loggedIn || isLoggedInFreeUser(user)) {
      return (
        <div
          className="transfer__option transfer__option--disabled"
          onClick={(e) => this.gotoPlus(e, user, 'expiry')}
        >
          <label>{I18n.t('transfer.options.expiry')}</label>
          <span className="transfer__option-placeholder">
            {this.expiryOptions[this.props.expiry]}{' '}
            <Icon type="Arrow" orientation="bottom" thickness={2} />
          </span>
          <Icon type="TransferPlus" backgroundColor={SUN} />
        </div>
      );
    }

    const expiryOptionsMenu = (
      <ul ref={this.expiryOptionsMenuRef} role="listbox" id="change-expiry">
        {Object.keys(this.expiryOptions).map((expiryKey) => {
          const isCurrentExpirySelected = this.props.expiry === expiryKey;
          const className = classNames({
            'tooltip__active-item': isCurrentExpirySelected,
          });
          return (
            <li
              role="option"
              aria-selected={isCurrentExpirySelected}
              key={expiryKey}
              className={className}
              onClick={() => this.changeExpiry(expiryKey)}
              tabIndex="-1"
              onKeyDown={(event) => this.onOptionKeyDown(event, expiryKey)}
            >
              {this.expiryOptions[expiryKey]}
            </li>
          );
        })}
      </ul>
    );

    return (
      <div className="transfer__option">
        <label id="change-expiry-label">
          {I18n.t('transfer.options.expiry')}
        </label>
        <PopTip
          anchor="bottom"
          offset={10}
          trigger="click"
          content={expiryOptionsMenu}
          visible={this.state.isPoptipVisible}
        >
          <div className="transfer__exp-option">
            <button
              aria-expanded={this.state.isPoptipVisible}
              aria-controls="change-expiry"
              aria-labelledby="change-expiry-label current-expiry"
              className="transfer__exp-option-label"
              ref={this.transferExpiryOptionsRef}
              onKeyDown={this.onTransferExpiryKeyDown}
            >
              <span id="current-expiry">
                {this.expiryOptions[this.props.expiry]}
              </span>
            </button>
            <Icon
              type="Arrow"
              orientation="bottom"
              thickness={2}
              color={WOODSMOKE}
            />
          </div>
        </PopTip>
      </div>
    );
  }

  renderPassword(user) {
    if (!user.loggedIn || isLoggedInFreeUser(user)) {
      return (
        <div
          className="transfer__option transfer__option--disabled"
          onClick={(e) => this.gotoPlus(e, user, 'password')}
        >
          <label>{I18n.t('transfer.options.password')}</label>
          <span className="transfer__option-placeholder">
            {I18n.t('transfer.options.password_placeholder')}
          </span>
          <Icon type="TransferPlus" backgroundColor={SUN} />
        </div>
      );
    }

    const pseudoRandomId = `id_${generatePseudoRandomNumber()}`;

    return (
      <div className="transfer__option">
        <label id={pseudoRandomId} htmlFor="transfer__password">
          {I18n.t('transfer.options.password')}
        </label>
        <input
          aria-labelledby={pseudoRandomId}
          id="transfer__password"
          type="text"
          onChange={this.changePassword}
          defaultValue={this.props.password}
          autoComplete="off"
          spellCheck="false"
          placeholder={I18n.t('transfer.options.password_placeholder')}
          className="transfer__password"
          ref={this.passwordRef}
        />
      </div>
    );
  }

  handleRadioButtonKeyUp = (event) => {
    if (
      !['ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowUp'].includes(event.key)
    ) {
      return;
    }

    event.preventDefault();

    const { transfer } = this.props;

    const transferTypes = ['email', 'link'];
    const currentTypeIndex = transferTypes.indexOf(transfer.type);
    const nextTypeIndex = (currentTypeIndex + 1) % transferTypes.length;
    const transferType = transferTypes[nextTypeIndex];

    this.transferTypeNodes[transferType].current.focus();
    this.switchTransferType(transferType);
  };

  render() {
    const { expiry, ui, password, type, user, isRecipientLocked } = this.props;

    if (!ui.uploader.options.visible) {
      return false;
    }

    const email = I18n.t('transfer.options.email_transfer');
    const link = I18n.t('transfer.options.transfer_link');

    const pseudoRandomId = `id_${generatePseudoRandomNumber()}`;

    return (
      <div ref={(node) => (this.node = node)} className="transfer__options">
        {!isRecipientLocked && (
          <div
            className="transfer__option transfer__type"
            onMouseEnter={this.showTransferTypeTip}
            onMouseLeave={this.hideTransferTypeTip}
          >
            <div
              tabIndex="-1"
              role="radiogroup"
              aria-labelledby={pseudoRandomId}
            >
              <Radio
                ref={this.transferTypeNodes.email}
                className="transfer__type-radio"
                name="transfer__type-email"
                label={email}
                checked={type === 'email'}
                onChange={() => this.switchTransferType('email')}
                onKeyUp={this.handleRadioButtonKeyUp}
              />
              <Radio
                ref={this.transferTypeNodes.link}
                className="transfer__type-radio"
                name="transfer__type-link"
                label={link}
                checked={type === 'link'}
                onChange={() => this.switchTransferType('link')}
                onKeyUp={this.handleRadioButtonKeyUp}
              />
            </div>
          </div>
        )}
        <div
          className="transfer__plus-features"
          onMouseEnter={this.showPlusTooltip}
        >
          {this.renderExpiry(user, expiry)}
          {this.renderPassword(user, password)}
        </div>
      </div>
    );
  }
}

Options.propTypes = {
  changeExpiry: PropTypes.func.isRequired,
  changePassword: PropTypes.func.isRequired,
  changeType: PropTypes.func.isRequired,
  clearTransferNotification: PropTypes.func.isRequired,
  expiry: PropTypes.string,
  onTransferType: PropTypes.func,
  password: PropTypes.string,
  pushTransferNotification: PropTypes.func.isRequired,
  transfer: PropTypes.object,
  type: PropTypes.string,
  user: PropTypes.object,
  ui: PropTypes.object,
  isRecipientLocked: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ user, transfer, ui }) => ({ user, transfer, ui });

const mapDispatchToProps = {
  changePassword: Actions.Transfer.changePassword,
  changeExpiry: Actions.Transfer.changeExpiry,
  changeType: Actions.Transfer.changeType,
  clearTransferNotification: Actions.Notification.clearTransfer,
  pushTransferNotification: Actions.Notification.pushTransfer,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(Options);
